/**
 * Mapping of user emails/usernames to their allowed clientIds.
 * Use "*" to allow access to all clients.
 * Usernames/emails are checked in a case-insensitive manner.
 */
export const USER_ACCESS_MAP = {
  "harry.maher@9foundations.co": ["*"],
  "harry.maher@9foundations.com": ["*"],
  "harry.maher@9foundations": ["*"],
  "preview": ["*"],
  "example.user@gmail.com": ["Capsule"],
  "capsule": ["Capsule"],
  "demo.user@9foundations.co": ["Demo"],
  "bcbs.user@gmail.com": ["BCBS"],
};

/**
 * List of domains where any user automatically gets access to all clients.
 */
export const WILDCARD_DOMAINS = [
  "9foundations.co",
  "9foundations.com",
];

/**
 * Checks if a user has access to a specific clientId.
 * 
 * @param {object} user - The Auth0 user object (contains email, name, nickname, etc.)
 * @param {string} clientId - The clientId being requested (e.g. "Demo", "Capsule", "BCBS")
 * @returns {boolean} - True if the user has access, false otherwise.
 */
export function hasPageAccess(user, clientId) {
  if (!user) return false;

  const email = (user.email || "").toLowerCase().trim();
  const nickname = (user.nickname || "").toLowerCase().trim();
  const name = (user.name || "").toLowerCase().trim();

  // 1. Check if user belongs to a wildcard domain
  if (email) {
    const domain = email.split("@")[1];
    if (domain && WILDCARD_DOMAINS.includes(domain)) {
      return true;
    }
  }

  // Helper to check access in the USER_ACCESS_MAP
  const checkAccess = (identifier) => {
    if (!identifier) return false;
    const allowed = USER_ACCESS_MAP[identifier];
    if (allowed) {
      if (allowed.includes("*")) return true;
      // Compare case-insensitively with the requested clientId
      return allowed.map(c => c.toLowerCase()).includes(clientId.toLowerCase());
    }
    return false;
  };

  // 2. Check by email, nickname, or name
  if (checkAccess(email)) return true;
  if (checkAccess(nickname)) return true;
  if (checkAccess(name)) return true;

  return false;
}

/**
 * Get list of all clientIds a user has access to based on their credentials and backend CLIENT_CONFIG keys.
 * 
 * @param {object} user - The Auth0 user object
 * @param {Array<string>} allClients - List of all possible clientIds (from CLIENT_CONFIG keys)
 * @returns {Array<string>} - List of clientIds the user has access to
 */
export function getAllowedClients(user, allClients = []) {
  if (!user) return [];
  return allClients.filter(clientId => hasPageAccess(user, clientId));
}
